if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupVariantPreviews();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if ((this.dataset.desktopLayout.includes('thumbnail')) && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
              if (this.elements.thumbnails.resetPages) this.elements.thumbnails.resetPages();
            }
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (options.skipPageScroll || activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail && options.announce !== false) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setupVariantPreviews() {
        const productSection = this.closest('section');
        const variantPicker = productSection?.querySelector('variant-selects[data-section]');
        if (!variantPicker) return;

        const selectedVariant = () => this.getVariantFromPicker(variantPicker);
        this.filterMediaByVariant(selectedVariant(), variantPicker);

        const preview = (event) => {
          const swatch = event.target.closest('[data-variant-media-id]');
          if (!swatch || !variantPicker.contains(swatch)) return;

          const hoveredInput = swatch.matches('input') ? swatch : document.getElementById(swatch.htmlFor);
          if (!hoveredInput) return;

          this.updateSwatchName(variantPicker, hoveredInput.name, hoveredInput.value);

          const variantData = variantPicker.getVariantData();
          const optionValues = variantData[0]?.options?.slice() || [];
          variantPicker.querySelectorAll('[data-option-position]').forEach((option) => {
            const optionIndex = Number(option.dataset.optionPosition) - 1;
            if (option.tagName === 'SELECT') {
              optionValues[optionIndex] = option.value;
            } else if (option.contains(hoveredInput)) {
              optionValues[optionIndex] = hoveredInput.value;
            } else {
              optionValues[optionIndex] = option.querySelector('input:checked')?.value;
            }
          });
          const hoveredVariant = variantData
            .find((variant) => variant.options.every((value, index) => value === optionValues[index]));

          if (!this.variantPreviewRestoreId) {
            const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
            this.variantPreviewRestoreId = activeMedia?.dataset.mediaId?.split('-').pop();
          }

          this.filterMediaByVariant(hoveredVariant, variantPicker);
          const variantMediaId = hoveredVariant?.featured_media?.id || swatch.dataset.variantMediaId;
          const mediaId = `${variantPicker.dataset.section}-${variantMediaId}`;
          if (!this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`)) return;

          this.setActiveMedia(mediaId, true, { skipPageScroll: true, announce: false });
        };

        const restore = (event) => {
          const swatch = event.target.closest('[data-variant-media-id]');
          if (!swatch || swatch.contains(event.relatedTarget)) return;

          this.restoreSwatchNames(variantPicker);
          this.filterMediaByVariant(selectedVariant(), variantPicker);
          if (!this.variantPreviewRestoreId) return;

          this.setActiveMedia(
            `${variantPicker.dataset.section}-${this.variantPreviewRestoreId}`,
            true,
            { skipPageScroll: true, announce: false }
          );
          this.variantPreviewRestoreId = null;
        };

        variantPicker.addEventListener('pointerover', preview);
        variantPicker.addEventListener('pointerout', restore);
        variantPicker.addEventListener('focusin', preview);
        variantPicker.addEventListener('focusout', restore);
        variantPicker.addEventListener('change', () => {
          this.variantPreviewRestoreId = null;
          window.queueMicrotask(() => {
            this.restoreSwatchNames(variantPicker);
            this.filterMediaByVariant(selectedVariant(), variantPicker);
          });
        });
      }

      getVariantFromPicker(variantPicker) {
        const options = [];
        variantPicker.querySelectorAll('[data-option-position]').forEach((option) => {
          const optionIndex = Number(option.dataset.optionPosition) - 1;
          options[optionIndex] =
            option.tagName === 'SELECT' ? option.value : option.querySelector('input:checked')?.value;
        });
        return variantPicker
          .getVariantData()
          .find((variant) => variant.options.every((value, index) => value === options[index]));
      }

      updateSwatchName(variantPicker, optionName, optionValue) {
        const selectedName = Array.from(variantPicker.querySelectorAll('[data-selected-swatch-value]')).find(
          (element) => element.dataset.selectedSwatchValue === optionName
        );
        if (selectedName) selectedName.textContent = optionValue;
      }

      restoreSwatchNames(variantPicker) {
        variantPicker.querySelectorAll('fieldset[data-option-position]').forEach((fieldset) => {
          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput) this.updateSwatchName(variantPicker, checkedInput.name, checkedInput.value);
        });
      }

      filterMediaByVariant(variant, variantPicker) {
        // Limit filtering to gallery slides. Image zoom buttons also carry a
        // data-media-id and must not count toward the visible-media fallback.
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        );
        if (!mediaItems.length) return;

        const variantData = variantPicker.getVariantData();
        const normalizeSku = (value) => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
        const variantSkus = [...new Set(variantData.map((item) => normalizeSku(item.sku)).filter(Boolean))].sort(
          (a, b) => b.length - a.length
        );
        const mediaSku = new Map(
          mediaItems.map((item) => {
            const normalizedFilename = normalizeSku(item.dataset.mediaFilename);
            return [item.dataset.mediaId, variantSkus.find((sku) => normalizedFilename.startsWith(sku))];
          })
        );
        const selectedSku = normalizeSku(variant?.sku);
        const selectedItem = variant?.featured_media?.id
          ? mediaItems.find(
              (item) => item.dataset.mediaId === `${variantPicker.dataset.section}-${variant.featured_media.id}`
            )
          : null;
        const skuMatchedItems = selectedSku
          ? mediaItems.filter((item) => mediaSku.get(item.dataset.mediaId) === selectedSku)
          : [];

        let visibleItems = mediaItems;
        if (skuMatchedItems.length) {
          // Shopify exposes one featured image per variant. Additional backend
          // images use the SKU in their filename (for example HK160145-01.jpg).
          // Treat those as belonging to the selected variant regardless of
          // their position in the product media list.
          visibleItems = selectedItem
            ? [selectedItem, ...skuMatchedItems.filter((item) => item !== selectedItem)]
            : skuMatchedItems;
        } else if (selectedItem) {
          const selectedPosition = Number(selectedItem?.dataset.mediaPosition);
          const variantPositions = [...new Set(variantData.flatMap((item) => {
            if (!item.featured_media?.id) return [];
            const mediaItem = mediaItems.find(
              (node) => node.dataset.mediaId === `${variantPicker.dataset.section}-${item.featured_media.id}`
            );
            return mediaItem ? [Number(mediaItem.dataset.mediaPosition)] : [];
          }))].sort((a, b) => a - b);
          const nextPosition = variantPositions.find((position) => position > selectedPosition) || Infinity;

          if (selectedItem && Number.isFinite(selectedPosition)) {
            visibleItems = mediaItems.filter((item) => {
              const position = Number(item.dataset.mediaPosition);
              const matchedSku = mediaSku.get(item.dataset.mediaId);
              return (
                position >= selectedPosition &&
                position < nextPosition &&
                (!matchedSku || matchedSku === selectedSku)
              );
            });
          }
        }

        // A variant can have only its featured image assigned in Shopify, or no
        // assigned image at all. Keep the variant-only gallery when it contains
        // at least two media items; otherwise supplement it so the PDP never
        // becomes a one-image gallery. Prefer shared/unassigned product media,
        // then use media assigned to another variant as the final fallback.
        const minimumVisibleMedia = Math.min(2, mediaItems.length);
        if (visibleItems.length < minimumVisibleMedia) {
          const visibleMediaIds = new Set(visibleItems.map((item) => item.dataset.mediaId));
          const variantSpecificMediaIds = new Set(
            variantData.flatMap((item) =>
              item.featured_media?.id ? [`${variantPicker.dataset.section}-${item.featured_media.id}`] : []
            )
          );
          mediaSku.forEach((sku, mediaId) => {
            if (sku) variantSpecificMediaIds.add(mediaId);
          });
          const fallbackItems = mediaItems
            .filter((item) => !visibleMediaIds.has(item.dataset.mediaId))
            .sort(
              (a, b) =>
                Number(variantSpecificMediaIds.has(a.dataset.mediaId)) -
                Number(variantSpecificMediaIds.has(b.dataset.mediaId))
            );

          visibleItems = visibleItems.concat(fallbackItems.slice(0, minimumVisibleMedia - visibleItems.length));
        }

        const visibleMediaIds = new Set(visibleItems.map((item) => item.dataset.mediaId));
        mediaItems.forEach((item) => item.toggleAttribute('hidden', !visibleMediaIds.has(item.dataset.mediaId)));

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
            thumbnail.toggleAttribute('hidden', !visibleMediaIds.has(thumbnail.dataset.target));
          });
          if (this.elements.thumbnails.resetPages) this.elements.thumbnails.resetPages();
        }
        if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();

        const activeItem = mediaItems.find((item) => item.classList.contains('is-active'));
        if (activeItem?.hidden && visibleItems[0]) {
          this.setActiveMedia(visibleItems[0].dataset.mediaId, false, { skipPageScroll: true, announce: false });
        }

        const counter = this.querySelector('.slider-counter--total');
        if (counter) counter.textContent = visibleItems.length;

        const modal = document.querySelector(`#ProductModal-${variantPicker.dataset.section}`);
        if (modal) {
          const rawVisibleIds = new Set(Array.from(visibleMediaIds, (id) => id.split('-').pop()));
          modal.querySelectorAll('[data-media-id]').forEach((item) => {
            item.toggleAttribute('hidden', !rawVisibleIds.has(String(item.dataset.mediaId)));
          });
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
